// components/omore/OmoreRecentSessions.tsx
// List of recent Ask Omore sessions with drill-down to detail

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchRecentSessions } from '../../services/omoreInteractionService';
import type { OmoreSession } from '../../types/OmoreInteractionTypes';
import './OmoreRecentSessions.css';

export const OmoreRecentSessions: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sessions, setSessions] = useState<OmoreSession[]>([]);

    useEffect(() => {
        loadSessions();
    }, []);

    const loadSessions = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await fetchRecentSessions(50);
            setSessions(data);
        } catch (err: any) {
            console.error('Error loading sessions:', err);
            setError(err.message || 'Failed to load sessions');
        } finally {
            setLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (error) {
            console.error('Failed to sign out:', error);
        }
    };

    const formatTimestamp = (ts: any): string => {
        if (!ts) return 'N/A';
        try {
            const date = ts.toDate ? ts.toDate() : new Date(ts);
            return date.toLocaleString();
        } catch {
            return 'N/A';
        }
    };

    const durationFormatted = (seconds: number): string => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}m ${s}s`;
    };

    return (
        <div className="omore-sessions-container">
            <header className="omore-sessions-header">
                <div className="header-content">
                    <div>
                        <h1>Recent Sessions</h1>
                        <p className="header-subtitle">Latest Ask Omore conversation sessions</p>
                    </div>
                    <div className="header-actions">
                        <button onClick={() => navigate('/omore')} className="back-button">← Omore Analytics</button>
                        <button onClick={loadSessions} className="refresh-button">↻ Refresh</button>
                        <span className="user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="logout-button">Sign Out</button>
                    </div>
                </div>
            </header>

            <div className="omore-sessions-main">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading sessions...</p>
                    </div>
                )}

                {error && (
                    <div className="error-state">
                        <p>{error}</p>
                        <button onClick={loadSessions} className="retry-button">Retry</button>
                    </div>
                )}

                {!loading && !error && sessions.length === 0 && (
                    <div className="empty-state">
                        <div className="empty-icon">💬</div>
                        <h3>No sessions</h3>
                        <p>No Omore sessions have been recorded yet.</p>
                    </div>
                )}

                {!loading && !error && sessions.length > 0 && (
                    <div className="sessions-table-wrapper">
                        <table className="sessions-table">
                            <thead>
                                <tr>
                                    <th>Explorer</th>
                                    <th>Helper Type</th>
                                    <th>Origin</th>
                                    <th>Messages</th>
                                    <th>Duration</th>
                                    <th>Tokens</th>
                                    <th>Suggestions</th>
                                    <th>Flagged</th>
                                    <th>Started</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sessions.map(session => (
                                    <tr key={session.id} className={session.hasModerationFlag ? 'flagged-row' : ''}>
                                        <td className="explorer-cell">
                                            <div className="explorer-name">{session.explorerName}</div>
                                        </td>
                                        <td>{session.helperType}</td>
                                        <td>{session.origin}</td>
                                        <td>{session.messageCount}</td>
                                        <td>{durationFormatted(session.durationSeconds)}</td>
                                        <td>{session.totalTokensUsed}</td>
                                        <td>
                                            {session.suggestionsShown > 0
                                                ? `${session.suggestionsAdopted}/${session.suggestionsShown}`
                                                : '—'}
                                        </td>
                                        <td>
                                            {session.hasModerationFlag
                                                ? <span className="flag-indicator">⚠️ Yes</span>
                                                : <span className="no-flag">—</span>
                                            }
                                        </td>
                                        <td>{formatTimestamp(session.startedAt)}</td>
                                        <td>
                                            <button
                                                onClick={() => navigate(`/omore/sessions/${session.id}`)}
                                                className="action-button view-button"
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
